import { useEffect, useRef } from "react";

/**
 * Read a Hub page again while somebody is looking at it, so it carries no button for it.
 *
 * Once when the window comes back to the front (the tab was left, a mail was opened in another one), and then every
 * `every` ms while the window is in front. The Hub has no stream from an Agent or from the account's own services, so
 * this is the one place a Hub page asks a clock; an Agent's own pages are told by their stream instead
 * (`lib/settingsFeed.ts`). A failed read is the reader's to keep quiet about: what is on screen stays.
 */
export function useAutoRefresh(read: () => void, { every = 30_000, active = true }: { every?: number; active?: boolean } = {}) {
  const latest = useRef(read);
  latest.current = read;
  useEffect(() => {
    if (!active) return;
    let timer: number | undefined;
    let last = Date.now();
    const again = () => { last = Date.now(); latest.current(); };
    const arm = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => { if (document.visibilityState === "visible") again(); arm(); }, every);
    };
    // The focus and the visibility event arrive together; one read is enough.
    const back = () => {
      if (document.visibilityState !== "visible" || Date.now() - last < 2000) return;
      again();
      arm();
    };
    arm();
    window.addEventListener("focus", back);
    document.addEventListener("visibilitychange", back);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("focus", back);
      document.removeEventListener("visibilitychange", back);
    };
  }, [active, every]);
}
